import { QuizAnswerConfig, QuizAnswerType, QuizImageAspectRatio, QuizQuestionConfig } from "../Shared/Party/PartyConfigData";
import { QuizPanelDisplayProps } from "./QuizPanel";
import { getCombinedTextCoverages, getOuterTextCoverage, MeasureContext, TextCoverage } from "./TextLayoutEngine";
import { UIConfig } from "./UIConfigContext";


type QuizGridDefinition = {
    rows : number;
    columns : number;
}

type QuizSize = {
    width : number;
    height : number;
}

export type QuestionLayout = {
    width: number;
    height: number;
    questionProportion: number;
    questionTextHeight: number;
    questionInnerTextHeight : number;
    questionImageGrid: QuizGridDefinition;
    questionImageGridHeight : number;
    questionImageSize: QuizSize;
    questionFontSize: number;
    answerGrid: QuizGridDefinition;
    answerTextHeights : number[];
    answerGridHeight : number;
    answerImageSize: QuizSize;
    answerFontSize: number;
    imagePadding: number;
    textPadding : number;
    performance: number;
}

type TextGridFit = { 
    grid : QuizGridDefinition,
    textSize : QuizSize, 
    gridSize : QuizSize
}


const convertAspectToRatio = (aspect: QuizImageAspectRatio): number => {
    switch (aspect) {
        case "21:9":
            return 2.333;
        case "16:9":
            return 1.778;
        case "3:2":
            return 1.5;
        case "4:3":
            return 1.333;
        case "1:1":
            return 1;
        case "3:4":
            return 0.75;
        case "2:3":
            return 0.66;
        case "9:16":
            return 0.563;
        default:
            return 0.429;
    }
}


const findHeightIfJustText = (width: number, n: number, textCoverage: Array<TextCoverage>, imagePadding: number, textPadding : number): TextGridFit => {

    // Given n objects lay out all possible grids starting with [n.1] and ending with [1,n]
    var grids = new Array<QuizGridDefinition>();
    for (var i = n; i > 0; i--) {
        if (i == n)
            grids.push({ columns : n,  rows : 1});
        else if (i == 1)
            grids.push({ columns : 1, rows : n});
        else {
            const c = Math.ceil(n / i);
            grids.push({ columns : i, rows : c});
        }
    }

    let minHeight = 5000;
    let minHeightIndex = 0;
    const textSize : QuizSize = { width : 0, height : 0};

    for (var i = 0; i < grids.length; i++) {
        // Take full width divide amoungst cells remembering there are columns + 1 padding columns
        const cellWidth = (width - 2 * imagePadding) / grids[i].columns - imagePadding;
        //Find the shoetest height of text that fits in the current cell width.
        let txCi = textCoverage.length - 1;
        for (var j = textCoverage.length - 2; j >= 0; j--) {
            if (cellWidth < textCoverage[j].minWidth)
                j = -1;
            else
                txCi = j;
        }

        // height  of grid is cellHeight (which is text height) + rows + 1 padding rows
        const cellHeight = textCoverage[txCi].height;
        const height = (cellHeight + 3 * textPadding) * grids[i].rows + textPadding;


        if (height < minHeight) {
            minHeight = height;
            minHeightIndex = i;
            textSize.width = textCoverage[txCi].minWidth;
            textSize.height = textCoverage[txCi].height;
        }
    }
    return { grid : grids[minHeightIndex], textSize : textSize, gridSize : { width : width , height : minHeight}} ;

}

type BestFitGrid = {
    grid : QuizGridDefinition;
    imageSize : QuizSize;
    maxArea : number;
    naturalGridHeight : number;
}

const getMaxSize = (width : number, height : number, aspect : number) : QuizSize => {

    const widthFromHeight = height * aspect;
    
    if(widthFromHeight <= width) 
        return { width : widthFromHeight, height : height};
    else
        return { width : width, height : width / aspect};
}

const findBestGridWithImages = (width: number, maxHeight : number, n: number, aspect: QuizImageAspectRatio, textCoverage: Array<TextCoverage>, imagePadding: number, textPadding : number): BestFitGrid => {
    
    // Given n objects lay out all possible grids starting with [n.1] and ending with [1,n]
    var grids = new Array<QuizGridDefinition>();
    for (var i = n; i > 0; i--) {
        if (i == n)
            grids.push({ columns : n, rows : 1});
        else if (i == 1)
            grids.push( { columns : 1, rows : n});
        else {
            const c = Math.ceil(n / i);
            grids.push({columns : i, rows : c});
        }
    }


    const areas = new Array<number>();
    const ar = convertAspectToRatio(aspect);
    let maxArea = 0;
    let maxAreaIndex = 0;
    let imageWidthAtMax = 0;
    let imageHeightAtMax = 0;
    var minHeightAtMax = 0;
    var minTextHeight = 0;

    for (var i = 0; i < grids.length; i++) {

        //Based on full height and width and remembering there are rows + 1 and columns + 1 padding rows and columns respectively calc cellWidth and Height
        const cellWidth = (width - imagePadding) / grids[i].columns - imagePadding;
        const gridWidth = grids[i].columns * cellWidth + imagePadding;
        
        const maxCellHeight = (maxHeight - imagePadding) / grids[i].rows - imagePadding;

        let txtHeight = 0;
         //Find the shoetest height of text that fits in the current cell width.

        if (textCoverage.length > 0) {

            let txCi = textCoverage.length - 1;
            for (var j = textCoverage.length - 2; j >= 0; j--) {
                if (cellWidth < textCoverage[j].minWidth)
                    j = -1;
                else
                    txCi = j;
            }

            txtHeight = textCoverage[txCi].height + textPadding;
        }

        // Based on the aspect ratio of the image, work out whether available height or width are the constraint and cal max Area of image.

        

        const imSize = getMaxSize(cellWidth, maxCellHeight, ar);
        const area = imSize.width * imSize.height;

        //store the grid that creates the largest image area.
        if (area > maxArea) {
            maxArea = area;
            maxAreaIndex = i;
            imageWidthAtMax = imSize.width;
            imageHeightAtMax = imSize.height;
            minTextHeight = txtHeight;

        }

    }
    const gridHeight = grids[maxAreaIndex].rows * (imageHeightAtMax + imagePadding + minTextHeight) + imagePadding;
    return { grid : grids[maxAreaIndex], maxArea, imageSize : { width : imageWidthAtMax, height : imageHeightAtMax}, naturalGridHeight : gridHeight};
}




const calcStackWithImages = (width: number, height: number, n: number, aspect: QuizImageAspectRatio, textCoverage: Array<TextCoverage>, imagePadding: number, textPadding : number): BestFitGrid=> {
    // Calulate cell width and height based on a single column layout 
    const cellWidth = width - 2 * imagePadding;
    var txtHeight = 0;
    if(textCoverage.length > 0) {
        txtHeight = textCoverage[0].height + textPadding;
    }
    const cellHeight = (height - imagePadding) / n - imagePadding;
    const ar = convertAspectToRatio(aspect);
    const imW = (cellHeight - txtHeight) * ar;
    const area = cellHeight * imW;
    return { grid : { columns : 1, rows : n} , maxArea : area, imageSize :  { width : imW, height : cellHeight}, naturalGridHeight : height};
}

//based on a fixed proportion between question and answer areas calc optimum layout
const calcLayoutResult = (width: number, height: number, proportion: number, qImages: number, qAspect: QuizImageAspectRatio, qTextCoverage: TextCoverage, answers : QuizAnswerConfig[], aAspect: QuizImageAspectRatio, aTextCoverage: Array<TextCoverage>, imagePadding: number, textPadding : number, questionFontSize: number, answerFontSize: number, answerType: QuizAnswerType, spaceWidth : number, measureContext : MeasureContext ): QuestionLayout => {
    var qHeight = height * proportion;
    var answerGridHeight = height - qHeight;
    const answerCount = answers.length;
    const answerGrid = (answerType == "select" || answerType == "matchAnswer") ? findBestGridWithImages(width, answerGridHeight, answerCount, aAspect, aTextCoverage, imagePadding,textPadding) : calcStackWithImages(width, height - qHeight, answerCount, aAspect, aTextCoverage, imagePadding, textPadding);
    if(aTextCoverage.length === 0)
    {
        qHeight = height - answerGrid.naturalGridHeight;
        answerGridHeight = answerGrid.naturalGridHeight;
    }
   
    
    //question is full width not in cells with images, so height calculated seperately and empty textcoveragge injrected
    const questionGrid = findBestGridWithImages(width, qHeight - qTextCoverage.height + 2 * textPadding + 0.5 * imagePadding, qImages, qAspect, new Array<TextCoverage>(), imagePadding, textPadding);
    const answerCellWidth = (width - imagePadding) / answerGrid.grid.columns - imagePadding;
    // sliders are full width hence the stack vs grid optional
    const ret: QuestionLayout = {
        width: width,
        height: height,
        questionProportion: proportion,
        questionTextHeight: height - answerGridHeight - questionGrid.naturalGridHeight,
        questionInnerTextHeight : qTextCoverage.height,
        questionImageGrid: questionGrid.grid,
        questionImageGridHeight : questionGrid.naturalGridHeight,
        questionImageSize: questionGrid.imageSize,
        questionFontSize: questionFontSize,
        answerGrid: answerGrid.grid,
        answerTextHeights : answers.map((answer) => { return answer.text === undefined ? 0 : (getOuterTextCoverage(answer.text, answerFontSize, answerFontSize * 1.2, spaceWidth, measureContext.measure, answerCellWidth)).height}),
        answerGridHeight : answerGridHeight,
        answerImageSize: answerGrid.imageSize,
        answerFontSize: answerFontSize,
        imagePadding : imagePadding,
        textPadding : textPadding,
        performance: questionGrid.maxArea + answerGrid.maxArea

    }
    return ret;
}


export const CreateQuestionLayout = (question: QuizQuestionConfig, currentAnswers: QuizAnswerConfig[], display: QuizPanelDisplayProps, uiConfig : UIConfig, measureContext: MeasureContext, headerHeight: number): QuestionLayout => {
    if(question.images === undefined)
        question.images = Array<string>();
    const answerType = question.answerType ?? "select";
    const height = display.availableHeight - headerHeight;
    
    const qTextCoverage = getOuterTextCoverage(question.text ?? "", measureContext.questionFontSize, measureContext.questionFontSize * 1.2, measureContext.spaceWidthQuestion, measureContext.measure, uiConfig.width);


    const answerText = new Array<string>();
    const questionHasImage = question.images !== undefined ? question.images.length > 0 : false;
    let answerHasImages = false;
    for (var i = 0; i < currentAnswers.length; i++) {
        const answer = currentAnswers[i];
        if (answer.text !== undefined)
            answerText.push(answer.text);
        if (answer.image !== undefined)
            answerHasImages = true;
    }

    const fontBaseAnswer = answerHasImages ? measureContext.answerMixedFontSize : measureContext.answerTextFontSize;
    const spaceWidthAnswer = answerHasImages ? measureContext.spaceWidthAnswerMixed : measureContext.spaceWidthAnswerText;

    const aTextCoverage = answerText.length > 0 ? getCombinedTextCoverages(answerText, fontBaseAnswer, fontBaseAnswer * 1.2, spaceWidthAnswer, measureContext.measure, uiConfig.width) : new Array<TextCoverage>();

    if (answerHasImages && questionHasImage) {

        const initial = [0.8, 0.75, 0.66, 0.6, 0.5, 0.4, 0.33, 0.25, 0.2];
        const initialRes = new Array<QuestionLayout>();
        let maxRes = 0;
        let maxResIndex = 0;
        for (var i = 0; i < initial.length; i++) {
            const res = calcLayoutResult(uiConfig.width, height, initial[i], question.images.length, question.questionImageAspect ?? "9:16", qTextCoverage, currentAnswers, question.answerImageAspect ?? '1:1', aTextCoverage, display.imagePadding, display.textPadding, measureContext.questionFontSize, measureContext.answerMixedFontSize, answerType, measureContext.spaceWidthAnswerMixed, measureContext);

            if (res.performance > maxRes) {
                maxRes = res.performance;
                maxResIndex = i;
            }
            initialRes.push(res);
        }

        const searchIndex = maxResIndex === 0 ? [1, 0] :
            (maxResIndex === (initial.length - 1) ? [initial.length - 1, initial.length - 2] :
                (initialRes[maxResIndex - 1].performance < initialRes[maxResIndex + 1].performance ? [maxResIndex + 1, maxResIndex] : [maxResIndex, maxResIndex - 1]));

        let searchRange = [initial[searchIndex[0], 0.5 * (initial[searchIndex[0]] + initial[searchIndex[1]]), searchIndex[1]]];

        let res = calcLayoutResult(uiConfig.width, uiConfig.width, searchRange[1], question.images.length, question.questionImageAspect ?? "9:16", qTextCoverage, currentAnswers, question.answerImageAspect ?? '1:1', aTextCoverage, display.imagePadding, display.textPadding, measureContext.questionFontSize, measureContext.answerMixedFontSize, answerType, measureContext.spaceWidthAnswerMixed, measureContext);

        let searchRes = [initialRes[searchIndex[0]].performance, res.performance, initialRes[searchIndex[1]].performance];

        while ((searchRange[1] - searchRange[0]) > 0.01) {
            const it1 = 0.5 * (searchRange[0] + searchRange[1]);
            const it2 = 0.5 * (searchRange[1] + searchRange[2]);
            const res1 = calcLayoutResult(uiConfig.width, height, it1, question.images.length, question.questionImageAspect ?? "9:16", qTextCoverage, currentAnswers, question.answerImageAspect ?? '1:1', aTextCoverage, display.imagePadding, display.textPadding, measureContext.questionFontSize, measureContext.answerMixedFontSize, answerType, measureContext.spaceWidthAnswerMixed, measureContext);
            const res2 = calcLayoutResult(uiConfig.width, height, it2, question.images.length, question.questionImageAspect ?? "9:16", qTextCoverage, currentAnswers, question.answerImageAspect ?? '1:1', aTextCoverage, display.imagePadding, display.textPadding, measureContext.questionFontSize, measureContext.answerMixedFontSize, answerType, measureContext.spaceWidthAnswerMixed, measureContext);

            if (res1.performance > res2.performance) {
                res = res1;
                searchRange = [searchRange[0], it1, searchRange[1]];
                searchRes = [searchRes[0], res1.performance, searchRange[1]];
            }
            else {
                res = res2;
                searchRange = [searchRange[1], it2, searchRange[2]];
                searchRes = [searchRes[1], res2.performance, searchRange[2]];
            }
        }

        return res;

    }
    else if (answerHasImages) {
        var qHeight = qTextCoverage.height + 2 * display.textPadding - 0.5 * display.imagePadding;
        var answerGridHeight = height - qHeight;
        const answerGrid = (answerType == "select" || answerType == "matchAnswer") ? findBestGridWithImages(uiConfig.width, answerGridHeight , currentAnswers.length, question.answerImageAspect ?? '1:1', aTextCoverage, display.imagePadding, display.textPadding) : calcStackWithImages(uiConfig.width, height - qHeight, currentAnswers.length, question.answerImageAspect ?? '1:1', aTextCoverage, display.imagePadding, display.textPadding);
        if(aTextCoverage.length === 0){
            qHeight = height - answerGrid.naturalGridHeight;
            answerGridHeight = answerGrid.naturalGridHeight;
        }
        const answerCellWidth = (uiConfig.width- display.imagePadding) / answerGrid.grid.columns - display.imagePadding;
        const ret: QuestionLayout = {
            width: uiConfig.width,
            height: height,
            questionProportion: qHeight / height,
            questionTextHeight: qHeight,
            questionInnerTextHeight : qTextCoverage.height,
            questionImageGrid: { columns : 0, rows : 0},
            questionImageGridHeight : 0,
            questionImageSize: { width : 0,  height : 0},
            questionFontSize: measureContext.questionFontSize,
            answerGrid: answerGrid.grid,
            answerTextHeights : currentAnswers.map((answer) => { return answer.text === undefined ? 0 : (getOuterTextCoverage(answer.text, measureContext.spaceWidthAnswerMixed, measureContext.answerMixedFontSize * 1.2, measureContext.answerMixedFontSize, measureContext.measure, answerCellWidth)).height}),
            answerGridHeight : answerGridHeight,
            answerImageSize: answerGrid.imageSize,
            answerFontSize: measureContext.answerMixedFontSize,
            imagePadding: display.imagePadding,
            textPadding : display.textPadding,
            performance: answerGrid.maxArea,
        }
        return ret;

    }
    else if (questionHasImage) {
        const answerGrid = findHeightIfJustText(uiConfig.width, currentAnswers.length, aTextCoverage, display.imagePadding, display.textPadding);
        const qHeight = height - answerGrid.gridSize.height;

        const questionGrid = findBestGridWithImages(uiConfig.width, qHeight - qTextCoverage.height - 2 * display.textPadding + 0.5 * display.imagePadding, question.images.length, question.questionImageAspect ?? "9:16", new Array<TextCoverage>(), display.imagePadding, display.textPadding);
        const qTextHeight = height - questionGrid.naturalGridHeight - answerGrid.gridSize.height;

        const answerCellWidth = (uiConfig.width- display.imagePadding) / answerGrid.grid.columns - display.imagePadding;
        const ret: QuestionLayout = {
            width: uiConfig.width,
            height: height,
            questionProportion: qHeight / height,
            questionTextHeight: qTextHeight,
            questionInnerTextHeight : qTextCoverage.height,
            questionImageGrid: questionGrid.grid,
            questionImageGridHeight : questionGrid.naturalGridHeight,
            questionImageSize: questionGrid.imageSize,
            questionFontSize: measureContext.questionFontSize,
            answerGrid: answerGrid.grid,
            answerGridHeight : answerGrid.gridSize.height,
            answerTextHeights : currentAnswers.map((answer) => { return answer.text === undefined ? 0 : (getOuterTextCoverage(answer.text, measureContext.spaceWidthAnswerMixed, measureContext.answerMixedFontSize * 1.2, measureContext.answerMixedFontSize, measureContext.measure, answerCellWidth)).height}),
            answerImageSize: { width : 0, height : 0},
            answerFontSize: measureContext.answerTextFontSize,
            imagePadding: display.imagePadding,
            textPadding : display.textPadding,
            performance: questionGrid.maxArea,
        }
        return ret;
    }
    else {
        const rqHeight = uiConfig.textLayout.getHeightsForColumns(measureContext.questionFontSize, 1,1, uiConfig.width - 2 * display.imagePadding, 2 * display.textPadding, question.text?? '')[0];
        const refHeight = height / (currentAnswers.length + 1); //qTextCoverage.height;
        const qHeight = (rqHeight + 2 * display.imagePadding < refHeight) ? refHeight : rqHeight + 2 * display.imagePadding;
        //const answerLayout = findHeightIfJustText(display.availableWidth, currentAnswers.length, aTextCoverage, display.padding);
        const answerCellWidth = uiConfig.width- display.imagePadding - display.imagePadding;
        const ret: QuestionLayout = {
            width: uiConfig.width,
            height: height,
            questionProportion: qHeight / height,
            questionTextHeight: qHeight,
            questionInnerTextHeight : rqHeight, //qTextCoverage.height,
            questionImageGrid: { rows : 0, columns : 0},
            questionImageGridHeight : 0,
            questionImageSize: { width : 0, height : 0},
            questionFontSize: measureContext.questionFontSize,
            answerGrid: { columns : 1, rows : currentAnswers.length}, //answerLayout.grid,
            answerTextHeights : currentAnswers.map((answer) => { return answer.text === undefined ? 0 : (getOuterTextCoverage(answer.text, measureContext.spaceWidthAnswerMixed, measureContext.answerMixedFontSize * 1.2, measureContext.answerMixedFontSize, measureContext.measure, answerCellWidth)).height}),
            answerGridHeight : height - qHeight,
            answerImageSize: { width : 0, height : 0},
            answerFontSize: measureContext.answerTextFontSize,
            imagePadding: display.imagePadding,
            textPadding : display.textPadding,
            performance: 0,
        }
        return ret;
    }
}